import { type IntelOrgAddMemberByEmailDto, IntelligencePersona } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { ApiErrorWrapper } from '@hooks/api.hook';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useAddIntelOrganizationMember } from '@queries/intel-organization.query';
import { createFormInvalidHandler } from '@utils/form.util';
import { INTELLIGENCE_PERSONA_OPTIONS } from '@utils/intelligence-persona.util';
import { type FC, useState } from 'react';
import { z } from 'zod';

type IntelTeamAddMemberModalProps = {
  isOpen: boolean;
  //
  onClose: () => void;
};

const intelTeamAddMemberSchema = z.object({
  email: z.string().trim().min(1, 'Email is required').email('Enter a valid email address'),
  persona: z.nativeEnum(IntelligencePersona, { required_error: 'Persona is required' }),
});

const intelTeamAddMemberDefaultValues = {
  email: '',
  persona: IntelligencePersona.MISSION_ENGINEER,
};

export const IntelTeamAddMemberModal: FC<IntelTeamAddMemberModalProps> = (props) => {
  const { isOpen, onClose } = props;

  const { mutateAsync: addMember } = useAddIntelOrganizationMember();
  const [isUnsavedConfirmOpen, setIsUnsavedConfirmOpen] = useState(false);

  const form = useAppForm({
    defaultValues: intelTeamAddMemberDefaultValues,
    onSubmit: async ({ value }) => {
      const payload: IntelOrgAddMemberByEmailDto = {
        email: value.email.trim(),
        persona: value.persona,
      };

      try {
        await addMember(payload);
        successToast({ description: 'Invitation sent.' });
        handleCloseInternal();
      } catch (error) {
        const message =
          error instanceof ApiErrorWrapper ? error.message : 'Unable to add member. Please try again later.';
        errorToast({ description: message });
      }
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: intelTeamAddMemberSchema,
    },
  });

  const handleCloseInternal = (): void => {
    form.reset();
    setIsUnsavedConfirmOpen(false);
    onClose();
  };

  const handleCloseRequest = (): void => {
    if (form.state.isDirty) {
      setIsUnsavedConfirmOpen(true);
      return;
    }
    handleCloseInternal();
  };

  return (
    <>
      <Modal
        centered
        footer={{
          rightButton: (
            <div className="flex justify-end">
              <form.Subscribe selector={(state) => state.isSubmitting}>
                {(isSubmitting) => (
                  <Button
                    disabled={isSubmitting}
                    onClick={() => void form.handleSubmit()}
                    type="button"
                  >
                    Add Member
                  </Button>
                )}
              </form.Subscribe>
            </div>
          ),
        }}
        header={{ title: 'Add Team Member' }}
        isOpen={isOpen}
        onClose={handleCloseRequest}
        width="480px"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <div className="flex flex-col gap-4">
              <form.AppField name="email">
                {(field) => (
                  <field.TextField
                    label="Email"
                    placeholder="analyst@agency.gov"
                    required
                    type="email"
                  />
                )}
              </form.AppField>

              <form.AppField name="persona">
                {(field) => (
                  <field.SelectField
                    label="Persona"
                    options={INTELLIGENCE_PERSONA_OPTIONS}
                    placeholder="Select a persona"
                    required
                  />
                )}
              </form.AppField>
            </div>
          </form.AppForm>
        </form>
      </Modal>
      <ConfirmationModal
        cancelLabel="Keep Editing"
        confirmLabel="Discard Changes"
        description="Discard this team member invitation?"
        isOpen={isUnsavedConfirmOpen}
        onConfirm={handleCloseInternal}
        setIsOpen={setIsUnsavedConfirmOpen}
        title="Discard changes?"
      />
    </>
  );
};
